import { useMemo } from "react";
import { MapPin } from "lucide-react";
import { KSANetworkMap } from "./KSANetworkMap";
import { locations } from "@/data/locations";
import { regionCenters } from "@/data/locations/regionCenters";

// Region → display city name mapping (matches DOOH map terminology)
const REGION_CITY: Record<string, string> = {
  Riyadh: "Riyadh",
  Jeddah: "Jeddah",
  Eastern: "Dammam",
  Medina: "Madinah",
  "Al Kharj": "Al Kharj",
  Dawadmi: "Dawadmi",
  Tabuk: "Tabuk",
  Hail: "Hail",
  "Hafar Al Batin": "Hafar Al Batin",
  Majmaa: "Majmaa",
  Qassim: "Buraydah",
  Sakaka: "Sakaka",
  Arar: "Arar",
  "Al Qurayyat": "Al Qurayyat",
  "Al Jubail": "Al Jubail",
  Yanbu: "Yanbu",
  "Khamis Mushait": "Khamis Mushait",
  Sabia: "Sabia",
  Abha: "Abha",
  Jizan: "Jizan",
  Najran: "Najran",
  Muhayil: "Muhayil",
  "Al Bahah": "Al Bahah",
};

export const CoverageMapSection = () => {
  const { cityList, totalFaces, ownerCount, mapCities } = useMemo(() => {
    const byRegion = new Map<string, number>();
    const owners = new Set<string>();
    let total = 0;

    for (const loc of locations) {
      if ((loc as any).adminOnly) continue;
      const devices = loc.devices || 0;
      total += devices;
      owners.add(loc.mediaOwner);
      byRegion.set(loc.region, (byRegion.get(loc.region) || 0) + devices);
    }

    const sorted = [...byRegion.entries()].sort((a, b) => b[1] - a[1]);

    const cityList = sorted.map(([region, faces]) => ({
      region,
      city: REGION_CITY[region] || region,
      faces,
    }));

    // Pin every city with a known geographic center on the SVG map
    // regionCenters uses [lat, lon]; KSANetworkMap expects { lon, lat }
    const mapCities = cityList
      .map((c) => {
        const center = (regionCenters as any)[c.region]?.center as
          | [number, number]
          | undefined;
        if (!center) return null;
        return { name: c.city, lat: center[0], lon: center[1] };
      })
      .filter(Boolean) as Array<{ name: string; lon: number; lat: number }>;

    return {
      cityList,
      totalFaces: total,
      ownerCount: owners.size,
      mapCities,
    };
  }, []);

  const summary = [
    { value: String(cityList.length), label: "Cities" },
    { value: String(ownerCount), label: "Media Partners" },
  ];

  return (
    <section className="rounded-3xl bg-card border border-border shadow-card p-6 md:p-10">
      <div className="max-w-2xl mb-8">
        <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] text-primary uppercase mb-3">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          Our Coverage
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
          Reaching every corner of <span className="text-primary">Saudi Arabia</span>
        </h2>
        <p className="text-muted-foreground text-sm md:text-base">
          A nationwide network of premium digital out-of-home faces, connecting
          your brand with audiences across the Kingdom.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 items-center">
        {/* Map */}
        <div className="lg:col-span-7">
          <div className="relative rounded-2xl bg-gradient-to-br from-[hsl(220_60%_8%)] via-[hsl(217_55%_16%)] to-[hsl(217_91%_24%)] border border-border/50 p-4 md:p-6 overflow-hidden">
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2 text-[10px] font-semibold tracking-widest uppercase text-white/70">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              Live Network
            </div>
            <KSANetworkMap width={720} height={520} showLabels cities={mapCities} connectAllToFirst={false} />
          </div>
        </div>

        {/* Side column */}
        <div className="lg:col-span-5 space-y-6">
          {/* Stat tiles */}
          <div className="grid grid-cols-2 gap-3">
            {summary.map((s) => (
              <div
                key={s.label}
                className="rounded-xl border border-border bg-background p-3 text-center"
              >
                <div className="text-xl md:text-2xl font-bold text-foreground">{s.value}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {/* City list - scrollable for full coverage */}
          <div className="rounded-2xl border border-border bg-background divide-y divide-border max-h-[420px] overflow-y-auto">
            {cityList.map((c) => (
              <div key={c.region} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                  </span>
                  <MapPin className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">{c.city}</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {c.faces.toLocaleString()} {c.faces === 1 ? "Face" : "Faces"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
